import React, { createContext, useContext, useEffect, useState } from 'react';

const ThemeContext = createContext();

export const ThemeProvider = ({ children }) => {
    // Читаем из localStorage или устанавливаем дефолтные значения
    const [theme, setTheme] = useState(() => {
        try {
            return localStorage.getItem('app-theme') || 'dark';
        } catch {
            return 'dark';
        }
    });
    const [accent, setAccent] = useState(() => {
        try {
            return localStorage.getItem('app-accent') || 'grass';
        } catch {
            return 'grass';
        }
    });
    // Independent of theme/accent - a layout density toggle, not a theme
    // variant, so it combines freely with any theme/accent (Light+Compact,
    // Dark+Compact, etc). See frontend-tasks/2026-09-02-compact-table-mode.md.
    // Temporarily disabled (forced off, toggle UI commented out in
    // Header.js/BottomNav.js/TournamentMenu.js) - not the code that caused
    // it, just parking the feature for now. Restore the line below to bring
    // it back.
    // const [compactTable, setCompactTable] = useState(() => {
    //     try {
    //         return localStorage.getItem('app-compact-table') === '1';
    //     } catch {
    //         return false;
    //     }
    // });
    const [compactTable, setCompactTable] = useState(false);
    // "Request desktop site", scoped to this app rather than a real
    // per-tab browser feature - App.js's own viewport-meta effect reads
    // this (see there) and swaps in a fixed-width viewport instead of
    // width=device-width, which is what actually makes mobile Chrome/
    // Safari lay the page out (and let you pinch-zoom/pan) as a wider
    // browser would instead of triggering our own responsive breakpoints.
    // A width in pixels (768/1920 - see Header.js's RESOLUTION_PRESETS)
    // rather than the old on/off boolean, so the +/- zoom control can step
    // through several preset widths instead of a single fixed one; 0 means
    // "off" (plain device-width). Lives here (not local state in Header.js,
    // where the zoom control UI is) because App.js's effect - the thing
    // that has to act on it - is a sibling of Header.js, not an ancestor;
    // ThemeContext already sits above both for exactly this kind of
    // cross-component synced setting.
    // NOT persisted here directly (unlike theme/accent above) - it's
    // scoped per-tournament, not a single global preference, and this
    // provider sits above the Router with no reactive access to which
    // tournament (if any) is currently open. Header.js - which already
    // computes tournamentId off the route on every render - owns the
    // actual `app-preview-width:<id>` localStorage read/write and calls
    // setPreviewWidth to restore it whenever tournamentId changes. Starts
    // at 0/off here purely as the safe default for the brief window before
    // that restore runs (and for pages with no tournamentId at all).
    //
    // Re-enabled as the old +/- stepper in Header.js's bottom nav (see its
    // own tournamentPinnedItems) - the single tap-to-cycle icon that used
    // to live in TournamentHero.js was the buggy one (that component sits
    // inside <Tournament>, which gets display:none'd behind the preview
    // iframe itself, so its copy of the control was duplicated into the
    // iframe with no connection back to the real state - see that file's
    // own commented-out block). Header.js/BottomNav.js are never hidden
    // that way, so a control living there doesn't have that problem.
    const [previewWidth, setPreviewWidth] = useState(0);

    // Mirrors TournamentPreviewFrame.js/App.js's own location postMessage
    // bridge, for theme/accent instead of the URL: the preview iframe is a
    // separate same-origin document with its OWN ThemeProvider instance,
    // which only ever reads localStorage once, at the iframe's own mount -
    // toggling the theme/accent switch in the real page afterwards changed
    // localStorage and this (outer) document's own data-theme attribute,
    // but the already-loaded iframe never heard about it and kept
    // rendering the old theme until the whole preview got reloaded.
    // preview_frame is read directly off window.location here (not passed
    // as a prop) since ThemeProvider sits above AppRoutes/isPreviewFrame in
    // App.js's tree.
    const isPreviewFrame = typeof window !== 'undefined'
        && new URLSearchParams(window.location.search).get('preview_frame') === '1';
    useEffect(() => {
        if (!isPreviewFrame) return;
        const handler = (event) => {
            if (event.origin !== window.location.origin) return;
            if (event.data?.type !== 'preview-theme-changed') return;
            if (event.data.theme) setTheme(event.data.theme);
            if (event.data.accent) setAccent(event.data.accent);
        };
        window.addEventListener('message', handler);
        return () => window.removeEventListener('message', handler);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isPreviewFrame]);

    // Эффект для переключения светлой/темной темы
    useEffect(() => {
        const root = document.documentElement;
        root.setAttribute('data-theme', theme);
        try {
            localStorage.setItem('app-theme', theme);
        } catch {}
    }, [theme]);

    // Эффект для переключения акцентного цвета (покрытия)
    useEffect(() => {
        const root = document.documentElement;
        root.setAttribute('data-accent', accent);
        try {
            localStorage.setItem('app-accent', accent);
        } catch {}
    }, [accent]);

    // Compact Table temporarily disabled - see the compactTable useState
    // above (forced false, setCompactTable is now a no-op in practice since
    // nothing calls it - toggle UI commented out everywhere). This replaces
    // the old sync-to-DOM effect: a browser that had it switched ON in an
    // earlier session still has data-compact-table sitting on <html> from
    // that visit (it's set directly on the DOM, independent of React state),
    // and with the old effect fully commented out nothing ever cleared it -
    // its [data-compact-table] CSS (padding/background overrides on
    // .toolbar-top-row etc.) kept applying even with the toggle hidden and
    // compactTable permanently false. Runs once on mount, unconditionally
    // removes it.
    useEffect(() => {
        document.documentElement.removeAttribute('data-compact-table');
        try {
            localStorage.removeItem('app-compact-table');
        } catch {}
    }, []);

    return (
        <ThemeContext.Provider value={{ theme, setTheme, accent, setAccent, compactTable, setCompactTable, previewWidth, setPreviewWidth }}>
            {children}
        </ThemeContext.Provider>
    );
};

// Хук для удобного использования в компонентах
export const useTheme = () => useContext(ThemeContext);
